import type { DailyLogStep } from "./constants";
import type { DailyLogFormValues } from "./schema";

function hasNonEmptyTrimmed(value: string): boolean {
  return value.trim().length > 0;
}

export function isOverviewStepComplete(values: DailyLogFormValues): boolean {
  return (
    hasNonEmptyTrimmed(values.location) &&
    hasNonEmptyTrimmed(values.date) &&
    hasNonEmptyTrimmed(values.weather) &&
    Number.isFinite(values.temperature)
  );
}

function allItemsHaveQuantity(items: { quantity: number }[]): boolean {
  return items.every((item) => item.quantity > 0);
}

export function isWorkforceStepComplete(values: DailyLogFormValues): boolean {
  return values.workforce_contractors.every((c) => allItemsHaveQuantity(c.workforces));
}

export function isEquipmentStepComplete(values: DailyLogFormValues): boolean {
  return values.equipment_contractors.every((c) => allItemsHaveQuantity(c.equipments));
}

export function isMilestoneReportStepComplete(values: DailyLogFormValues): boolean {
  // Strip HTML tags (TipTap stores HTML) before checking content
  const textContent = values.description.replace(/<[^>]*>/g, "");
  return hasNonEmptyTrimmed(textContent);
}

export function isSitePictureStepComplete(values: DailyLogFormValues): boolean {
  return values.image_urls.length > 0;
}

/** 현재 스텝에서 Next로 진행 가능한지 */
export function canProceedToNextStep(step: DailyLogStep, values: DailyLogFormValues): boolean {
  if (step === "Overview") {
    return isOverviewStepComplete(values);
  }
  if (step === "Workforce") {
    return isWorkforceStepComplete(values);
  }
  if (step === "Equipment") {
    return isEquipmentStepComplete(values);
  }
  if (step === "MilestoneReport") {
    return isMilestoneReportStepComplete(values);
  }
  if (step === "SitePicture") {
    return isSitePictureStepComplete(values);
  }
  return true;
}

/**
 * `fromIndex`부터 목표 직전 단계까지 `canProceedToNextStep`을 차례로 적용해
 * 처음 막히는 단계의 인덱스를 돌려준다. 모두 통과하면 null.
 */
export function findFirstBlockingStepIndex(
  steps: readonly DailyLogStep[],
  fromIndex: number,
  targetIndex: number,
  values: DailyLogFormValues,
): number | null {
  for (let index = fromIndex; index < targetIndex; index++) {
    const step = steps[index];
    if (step && !canProceedToNextStep(step, values)) return index;
  }
  return null;
}

export interface DailyLogRailStepState {
  isReachable: boolean;
  /** 현재 단계보다 앞이고 입력이 유효해 완료로 표시할지 */
  isCompleted: boolean;
}

/**
 * desktop 단계 rail의 단계별 상태. 방문 여부가 아니라 폼 값으로 판단한다 (linear stepper 관례).
 * - 현재 단계와 이전 단계로는 언제든 돌아갈 수 있다.
 * - 뒤 단계는 첫 단계부터 그 직전 단계까지 모두 유효할 때만 이동할 수 있다.
 *   초안을 불러와 앞 단계가 이미 채워져 있으면 처음 진입해도 바로 이동할 수 있고,
 *   앞 단계의 필수값을 지우면 방문했던 뒤 단계로도 건너뛸 수 없다.
 */
export function getDailyLogRailStepStates(
  steps: readonly DailyLogStep[],
  currentIndex: number,
  values: DailyLogFormValues,
): DailyLogRailStepState[] {
  const firstBlockingIndex = findFirstBlockingStepIndex(steps, 0, steps.length, values);
  return steps.map((step, index) => ({
    isReachable: index <= currentIndex || firstBlockingIndex === null || index <= firstBlockingIndex,
    isCompleted: index < currentIndex && canProceedToNextStep(step, values),
  }));
}
